"use client"

import { useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { AlertTriangle, ChevronDown, Home, RotateCw } from "lucide-react"
import { Button } from "@/components/ui/button"

const BRAND_GRADIENT = "from-[#6366f1] to-[#8b5cf6]"

interface ErrorViewProps {
  error: Error & { digest?: string }
  reset: () => void
  /** Large display code shown in the brand gradient. Defaults to "500". */
  code?: string
}

export function ErrorView({ error, reset, code = "500" }: ErrorViewProps) {
  const [showDetails, setShowDetails] = useState(false)
  const isDev = process.env.NODE_ENV === "development"

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4">
      <div className="w-full max-w-md flex flex-col items-center text-center">
        <div className="relative mb-10 h-[52px] w-[229px]">
          <Image
            src="/58146_DXTRlab_RM-02.png"
            alt="DXTR Lab"
            fill
            priority
            className="object-contain"
          />
        </div>

        <div className="w-full rounded-xl border border-border/60 bg-card/80 px-8 py-10 shadow-sm backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br ${BRAND_GRADIENT} text-white shadow-md`}
            >
              <AlertTriangle className="h-7 w-7" />
            </div>
            <span
              className={`bg-gradient-to-r ${BRAND_GRADIENT} bg-clip-text text-5xl font-bold tracking-tight text-transparent`}
            >
              {code}
            </span>
          </div>

          <h1 className="mt-5 text-lg font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            An unexpected error occurred. The issue has been reported to our team.
          </p>

          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild className="w-full sm:w-auto">
              <Link href="/">
                <Home className="h-4 w-4" /> Go to home
              </Link>
            </Button>
            <Button variant="outline" onClick={reset} className="w-full sm:w-auto">
              <RotateCw className="h-4 w-4" /> Try again
            </Button>
          </div>

          {(error.digest || isDev) && (
            <div className="mt-6 border-t border-border/60 pt-4 text-left">
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`}
                />
                Technical details
              </button>
              {showDetails && (
                <div className="mt-3 rounded-md bg-muted px-3 py-2.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
                  {error.digest && (
                    <div>
                      <span className="text-foreground/70">reference:</span>{" "}
                      {error.digest}
                    </div>
                  )}
                  {isDev && error.message && (
                    <div className="mt-1 break-words whitespace-pre-wrap">
                      <span className="text-foreground/70">message:</span>{" "}
                      {error.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
